import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { MAX_REPLY_DEPTH, NEED_PROFILE, store, useDB } from '../data/store';
import { me, statsFor, thread, userById, type ThreadNode } from '../data/queries';
import type { Format, Post } from '../data/types';
import { BRANCHES, BRANCH_COLOR, type Branch } from '../lib/validation';
import { extractTags } from '../lib/text';
import { YEARS, yearColor } from '../lib/years';
import { SITE_URL } from '../lib/site';
import { Avatar, ConfirmButton, ExampleLabel, Icon, RichText, Stamp, TagLink, YearLink, timeAgo } from './Bits';
import { useUI } from './ui';

/** One memory as a dark print with the film date stamp glowing in its year's colour. */
export function PostCard({ post, full }: { post: Post; full?: boolean }) {
  const db = useDB(), ui = useUI(), nav = useNavigate();
  const author = userById(db, post.authorId);
  const stats = statsFor(db, post);
  const myId = db.meId;
  const reacted = !!myId && db.reactions.some(r => r.postId === post.id && r.userId === myId);
  const saved = !!myId && db.saves.some(s => s.postId === post.id && s.userId === myId);
  const mine = !!myId && post.authorId === myId;
  const community = post.communityId ? db.communities.find(c => c.id === post.communityId) : undefined;
  const idea = post.ideaId ? db.ideas.find(i => i.id === post.ideaId) : undefined;
  const kind = post.branch === 'Kindness';
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const out = (e: MouseEvent) => { if (!moreRef.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener('mousedown', out);
    return () => document.removeEventListener('mousedown', out);
  }, [menu]);
  const share = async () => {
    if (import.meta.env.MODE === 'artifact') { ui.toast('Sharing links works once the website is live.'); return; }
    const url = `${SITE_URL}/p/${post.id}`;
    try { await navigator.clipboard.writeText(url); ui.toast('Link copied'); } catch { ui.toast(url); }
  };
  return (
    <article className="card post" style={{ ['--yc' as string]: yearColor(post.year) }} aria-label={`Memory from ${post.year}: ${post.title}`}>
      <Stamp className="stamp" text={`'${String(post.year).slice(2)}`} label={String(post.year)} />
      <div className="meta">
        <Avatar name={author?.handle ?? '?'} color={author?.color ?? 'pink'} />
        <Link to={`/u/${author?.handle}`} className="who" style={{ color: `var(--rb-${author?.color ?? 'pink'})` }}>@{author?.handle}</Link>
        <span>filed under <YearLink year={post.year} /></span>
        <span style={{ color: `var(--rb-${BRANCH_COLOR[post.branch]})` }}>{post.branch}</span>
        {community && <Link to={`/c/${community.slug}`} className="badge">c/{community.slug}</Link>}
        {post.format === 'forgotten' && <span className="badge">{post.solved ? 'Found it' : 'Forgotten Forever?'}</span>}
        {post.format === 'there' && <span className="badge">You had to be there</span>}
        <span>{timeAgo(post.createdAt)}{post.editedAt ? ' · edited' : ''}</span>
        {post.example && <ExampleLabel />}
      </div>
      {editing ? <EditPost post={post} onDone={() => setEditing(false)} /> : <>
        <h3 className="ttl">{full ? post.title : <Link to={`/p/${post.id}`}>{post.title}</Link>}</h3>
        <p className="txt"><RichText text={post.body} /></p>
      </>}
      {post.photoNote && <div className="photo" role="img" aria-label={post.photoNote}>Example photo: {post.photoNote}</div>}
      {idea && <p className="small muted">Answer to the idea <Link to={`/ideas/${idea.id}`}>"{idea.text}"</Link></p>}
      <div className="tags">
        <span className="tag">{post.lived ? 'I lived this' : 'Before my time'}</span>
        {post.core && <span className="tag">Core memory</span>}
        {post.tags.map(t => <TagLink key={t} tag={t} />)}
      </div>
      <div className="acts">
        <button className={'act' + (reacted ? ' on' : '')} aria-pressed={reacted}
          onClick={() => ui.act(() => store.toggleReaction(post.id), on => { if (on && !kind) ui.toast('It haunts you too'); })}>
          <Icon name={kind ? 'heart' : 'ghost'} /><span>{kind ? 'Thank you' : 'I remember this'}</span><span className="n">{stats.reactions}</span>
        </button>
        {kind && <Link className="act" to={`/new?branch=Kindness&passed=${post.id}`}><Icon name="pass" /><span>Pass it on</span></Link>}
        {post.format === 'forgotten' && !post.solved && !mine && <Link className="act" to={`/p/${post.id}#reply`}><Icon name="know" /><span>I know what this is</span></Link>}
        <Link className="act" to={`/p/${post.id}`} aria-label={`${stats.comments} comments`}><Icon name="comment" /><span className="n">{stats.comments}</span></Link>
        <button className={'act' + (saved ? ' on' : '')} aria-pressed={saved}
          onClick={() => ui.act(() => store.toggleSave(post.id), on => ui.toast(on ? 'Saved to your Time Capsule' : 'Removed from your Time Capsule'))}>
          <Icon name="save" /><span>{saved ? 'Saved' : 'Save'}</span>
        </button>
        <button className="act" onClick={share} aria-label="Copy link"><Icon name="share" /></button>
        <div className="more" ref={moreRef}>
          <button className="act" aria-expanded={menu} aria-label="More options" onClick={() => setMenu(!menu)}><Icon name="more" /></button>
          {menu && <div className="menu" role="menu">
            {mine && <button role="menuitem" onClick={() => { setMenu(false); setEditing(true); }}>Edit</button>}
            {mine && post.format === 'forgotten' && !post.solved && <button role="menuitem" onClick={() => ui.act(() => store.markSolved(post.id), () => ui.toast('Marked as found'))}>Mark as found</button>}
            {mine && <ConfirmButton role="menuitem" label="Delete" confirmLabel="Tap again to delete for good" onConfirm={() => ui.act(() => store.deletePost(post.id), () => { setMenu(false); ui.toast('Deleted'); if (full) nav('/'); })} />}
            {!mine && <button role="menuitem" onClick={() => { setMenu(false); ui.report('post', post.id); }}>Report</button>}
            {!mine && author && <button role="menuitem" onClick={() => ui.act(() => store.toggleFollow('user', author.id), on => ui.toast(on ? `Following @${author.handle}` : `Unfollowed @${author.handle}`))}>Follow or unfollow @{author.handle}</button>}
          </div>}
        </div>
      </div>
    </article>
  );
}

function EditPost({ post, onDone }: { post: Post; onDone: () => void }) {
  const ui = useUI();
  const [title, setTitle] = useState(post.title), [body, setBody] = useState(post.body);
  return (
    <form className="form" onSubmit={e => { e.preventDefault(); ui.act(() => store.editPost(post.id, { title, body }), () => { ui.toast('Saved'); onDone(); }); }}>
      <input className="in" value={title} onChange={e => setTitle(e.target.value)} maxLength={80} aria-label="Name the thing" />
      <textarea className="in" value={body} onChange={e => setBody(e.target.value)} maxLength={1000} rows={4} aria-label="Memory" />
      <div className="row end"><button type="button" className="btn ghost sm" onClick={onDone}>Cancel</button><button className="btn hot sm">Save</button></div>
    </form>
  );
}

export type ComposerDefaults = { year?: number; branch?: Branch; title?: string; communityId?: string; ideaId?: string; passedFromId?: string; format?: Format };

/** Share a memory. Writing #anything creates that hashtag. */
export function Composer({ defaults = {}, compact, onPosted }: { defaults?: ComposerDefaults; compact?: boolean; onPosted?: (p: Post) => void }) {
  const db = useDB(), ui = useUI();
  const [f, setF] = useState({ title: defaults.title ?? '', body: '', year: defaults.year ? String(defaults.year) : '', branch: defaults.branch ?? 'Songs' as Branch,
    lived: true, core: false, hunt: defaults.format === 'forgotten', there: defaults.format === 'there', communityId: defaults.communityId ?? '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const tags = useMemo(() => extractTags(f.title + ' ' + f.body), [f.title, f.body]);
  const myCommunities = db.communities.filter(c => db.memberships.some(m => m.communityId === c.id && m.userId === db.meId));
  const passed = defaults.passedFromId ? db.posts.find(p => p.id === defaults.passedFromId) : undefined;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const input = { title: f.title, body: f.body, year: Number(f.year), branch: f.branch, lived: f.lived, core: f.core,
      format: f.hunt ? 'forgotten' : f.there ? 'there' : 'normal', communityId: f.communityId || undefined, ideaId: defaults.ideaId, passedFromId: defaults.passedFromId };
    if (!f.year) return setErr({ year: 'Pick the year this memory is from.' });
    const posted = (p: Post) => {
      setF({ ...f, title: '', body: '', core: false, hunt: false, there: false }); setErr({});
      ui.toast(`Filed under ${p.year}`); onPosted?.(p);
    };
    const r = store.createPost(input);
    if (r.ok) posted(r.value);
    else if (r.error === NEED_PROFILE) ui.act(() => store.createPost(input), posted);
    else setErr(r.fields ?? { form: r.error });
  };
  return (
    <form className={'card composer' + (compact ? ' compact' : '')} onSubmit={submit} noValidate>
      <label className="line" htmlFor="c-title">{passed ? `Pass it on: a kind thing inspired by @${userById(db, passed.authorId)?.handle}` : 'Share a memory'}</label>
      <input id="c-title" className="title-in" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} maxLength={80}
        placeholder="Name the thing: the school disco, the modem noise..." aria-invalid={!!err.title} />
      {err.title && <em className="error">{err.title}</em>}
      <textarea className="body-in" value={f.body} onChange={e => setF({ ...f, body: e.target.value })} maxLength={1000} rows={compact ? 2 : 4}
        placeholder="What do you remember? Add #hashtags to file it under topics. New ones are created automatically." aria-label="What do you remember?" aria-invalid={!!err.body} />
      {err.body && <em className="error">{err.body}</em>}
      {tags.length > 0 && <div className="tags" aria-label="Hashtags in this memory">{tags.map(t => <TagLink key={t} tag={t} />)}</div>}
      <div className="crow">
        <select value={f.year} onChange={e => setF({ ...f, year: e.target.value })} aria-label="Year it's about" aria-invalid={!!err.year}>
          <option value="">Year it's about</option>{YEARS.slice().reverse().map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={f.branch} onChange={e => setF({ ...f, branch: e.target.value as Branch })} aria-label="Topic">{BRANCHES.map(b => <option key={b}>{b}</option>)}</select>
        {myCommunities.length > 0 && <select value={f.communityId} onChange={e => setF({ ...f, communityId: e.target.value })} aria-label="Community">
          <option value="">No community</option>{myCommunities.map(c => <option key={c.id} value={c.id}>c/{c.slug}</option>)}</select>}
        <span className="seg" role="group" aria-label="Did you live it?">
          <button type="button" aria-pressed={f.lived} onClick={() => setF({ ...f, lived: true })}>I lived this</button>
          <button type="button" aria-pressed={!f.lived} onClick={() => setF({ ...f, lived: false })}>Before my time</button>
        </span>
      </div>
      <div className="crow">
        <label className="chk"><input type="checkbox" checked={f.core} onChange={e => setF({ ...f, core: e.target.checked })} />Core memory</label>
        <label className="chk"><input type="checkbox" checked={f.hunt} onChange={e => setF({ ...f, hunt: e.target.checked, there: false })} />I don't know what this is</label>
        <label className="chk"><input type="checkbox" checked={f.there} onChange={e => setF({ ...f, there: e.target.checked, hunt: false })} />You had to be there</label>
        <span className="grow" />
        {(err.year || err.form) && <em className="error">{err.year || err.form}</em>}
        <button className="btn hot">Post{f.year ? ` to ${f.year}` : ''}</button>
      </div>
      <p className="small muted">No photos of other people without their OK. Don't name people from your past who aren't here. <Link to="/guidelines">Guidelines</Link></p>
    </form>
  );
}

/** Comments with replies up to MAX_REPLY_DEPTH levels. */
export function Thread({ post }: { post: Post }) {
  const db = useDB();
  const nodes = useMemo(() => thread(db, post.id), [db, post.id]);
  return (
    <section className="thread" aria-label="Comments">
      <h2 className="h2" id="reply">Comments</h2>
      <CommentForm postId={post.id} />
      {nodes.length === 0 ? <p className="muted">No comments yet. Add the first memory to this one.</p>
        : <ul className="comments">{nodes.map(n => <CommentNode key={n.comment.id} node={n} postId={post.id} depth={0} />)}</ul>}
    </section>
  );
}
function CommentNode({ node, postId, depth }: { node: ThreadNode; postId: string; depth: number }) {
  const db = useDB(), ui = useUI();
  const c = node.comment, author = userById(db, c.authorId), mine = c.authorId === db.meId;
  const [replying, setReplying] = useState(false);
  return (
    <li className="comment">
      <div className="cmeta">
        {c.removed ? <span className="muted">Deleted comment</span> : <>
          <Link to={`/u/${author?.handle}`} className="who" style={{ color: `var(--rb-${author?.color ?? 'pink'})` }}>@{author?.handle}</Link>
          <span className="muted small">{timeAgo(c.createdAt)}</span>{c.example && <ExampleLabel />}</>}
      </div>
      {!c.removed && <p className="ctext"><RichText text={c.body} /></p>}
      {!c.removed && <div className="row small">
        {depth < MAX_REPLY_DEPTH - 1 && <button className="linkbtn" onClick={() => setReplying(!replying)}>Reply</button>}
        {mine ? <button className="linkbtn" onClick={() => ui.act(() => store.deleteComment(c.id), () => ui.toast('Comment deleted'))}>Delete</button>
          : <button className="linkbtn" onClick={() => ui.report('comment', c.id)}>Report</button>}
      </div>}
      {replying && <CommentForm postId={postId} parentId={c.id} onDone={() => setReplying(false)} />}
      {node.children.length > 0 && <ul className="comments">{node.children.map(k => <CommentNode key={k.comment.id} node={k} postId={postId} depth={depth + 1} />)}</ul>}
    </li>
  );
}
function CommentForm({ postId, parentId, onDone }: { postId: string; parentId?: string; onDone?: () => void }) {
  const ui = useUI(), db = useDB();
  const [body, setBody] = useState('');
  return (
    <form className="cform" onSubmit={e => { e.preventDefault(); ui.act(() => store.addComment(postId, { body }, parentId), () => { setBody(''); onDone?.(); }); }}>
      <input className="in" value={body} onChange={e => setBody(e.target.value)} maxLength={500} placeholder={parentId ? 'Write a reply' : me(db) ? 'Add your own memory to this one' : 'Add a comment'} aria-label={parentId ? 'Reply' : 'Comment'} />
      <button className="btn solid sm" disabled={!body.trim()}>{parentId ? 'Reply' : 'Comment'}</button>
    </form>
  );
}
